<template>
  <v-navigation-drawer
    v-model="drawer"
    temporary
    right
    width="450"
    height="100%"
    app
  >
    <v-card
      flat
      :loading="loading"
    >
      <v-card-title>
        <v-row>
          <v-col cols="12">
            <v-icon @click="drawer = false"> close </v-icon>
          </v-col>
        </v-row>
        <v-row v-if="!loading">
          <v-col>
            {{ template.name }}
          </v-col>
          <v-spacer class="limited-spacer" />
          <v-col
            cols="auto"
            class="my-auto"
          >
            <v-btn
              text
              @click="showRename"
            >
              Rename
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text v-if="!loading">
        <v-row dense>
          <v-col>
            <v-select
              v-model="template.published"
              :items="publishedItems"
              outlined
              dense
              :disabled="!template.imageFlatSide1Thumb"
              hide-details
              :loading="publishing"
              @change="savePublished"
            />
          </v-col>
          <v-spacer class="limited-spacer" />
          <v-col cols="auto">
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-btn
                  outlined
                  class="px-2"
                  min-width="20"
                  v-bind="attrs"
                  @click="openEditor"
                  v-on="on"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
              </template>
              <div class="px-4 py-2">Open in Editor</div>
            </v-tooltip>
          </v-col>
          <v-col
            v-if="template.published && !template.resizedFromTemplateId"
            cols="auto"
          >
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-btn
                  outlined
                  class="px-2"
                  min-width="20"
                  v-bind="attrs"
                  @click="showCopy"
                  v-on="on"
                >
                  <v-icon>content_copy</v-icon>
                </v-btn>
              </template>
              <div class="px-4 py-2">Copy to Different Size</div>
            </v-tooltip>
          </v-col>
          <v-col cols="auto">
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-btn
                  outlined
                  class="px-2"
                  min-width="20"
                  :color="refreshing || template.imagesGenerating ? 'error' : undefined"
                  v-bind="attrs"
                  @click="refreshImages"
                  v-on="on"
                >
                  <v-icon>auto_mode</v-icon>
                </v-btn>
              </template>
              <div class="px-4 py-2">Regenerate Images</div>
            </v-tooltip>
          </v-col>
          <v-col cols="auto">
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-btn
                  outlined
                  class="px-2"
                  min-width="20"
                  v-bind="attrs"
                  @click="deleteTemplate"
                  v-on="on"
                >
                  <v-icon>delete</v-icon>
                </v-btn>
              </template>
              <div class="px-4 py-2">Delete</div>
            </v-tooltip>
          </v-col>
          <v-col cols="auto">
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-btn
                  outlined
                  class="px-2"
                  min-width="20"
                  v-bind="attrs"
                  @click="refreshTemplate"
                  v-on="on"
                >
                  <v-icon>refresh</v-icon>
                </v-btn>
              </template>
              <div class="px-4 py-2">Refresh</div>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-slide-group
          class="py-4"
          show-arrows
        >
          <v-slide-item
            v-for="(image, i) in template.previews"
            :key="i"
            class="mr-4"
          >
            <v-img
              :src="image.url"
              max-width="300"
              class="cursor-pointer"
              @click="previewImages"
            />
          </v-slide-item>
        </v-slide-group>
      </v-card-text>
      <v-tabs
        v-model="tab"
        fixed-tabs
      >
        <v-tab
          v-for="(item, i) in tabs"
          :key="i"
        >
          {{ item.name }}
        </v-tab>
      </v-tabs>
      <v-divider />
      <v-card-text v-if="!loading">
        <v-tabs-items v-model="tab">
          <v-tab-item
            v-for="(item, i) in tabs"
            :key="i"
            class="pt-2"
          >
            <TemplateEditDetails
              v-if="item.id == 'details'"
              ref="details"
              @saving="saving = $event"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>
    <template #append>
      <div
        v-if="tab < 2"
        class="px-3 py-5"
      >
        <v-btn
          color="primary"
          block
          large
          :loading="saving"
          @click="save"
        >
          Save
        </v-btn>
      </div>
    </template>
    <RenameDialog
      ref="rename"
      title="Template"
      saveAction="templates/updateDetails"
    />
    <TemplateCopy @save="$emit('reload')" />
    <ImagePreview ref="imagePreview" />
  </v-navigation-drawer>
</template>

<script>
import ImagePreview from '@/components/common/ImagePreview';
import RenameDialog from '@/components/common/RenameDialog';
import TemplateCopy from '@/components/templates/Copy';
import TemplateEditDetails from '@/components/templates/EditDetails';
import { mapGetters } from 'vuex';
export default {
  name: 'TemplateEdit',
  components: {
    TemplateEditDetails,
    RenameDialog,
    TemplateCopy,
    ImagePreview
  },
  data() {
    return {
      loading: false,
      valid: false,
      drawer: false,
      saving: false,
      refreshing: false,
      publishing: false,
      publishedItems: [
        { value: true, text: 'Published' },
        { value: false, text: 'Not Published' }
      ],
      tab: 0,
      tabs: [
        { id: 'details', name: 'Details' },
        { id: 'activity', name: 'Activity' }
      ],
      converting: false
    };
  },

  computed: {
    ...mapGetters({
      template: 'templates/template'
    })
  },

  watch: {
    tab(val, oldVal) {
      if (val !== oldVal) {
        this.$store.dispatch('templates/edit', this.template);
      }
    }
  },

  mounted() {
    this.$bus.$off(this.$enums.EVENT_BUS_EVENTS.EDIT_TEMPLATE_SHOW);
    this.$bus.$on(this.$enums.EVENT_BUS_EVENTS.EDIT_TEMPLATE_SHOW, (data) => {
      this.loadTemplate(data.id);
      this.drawer = true;
    });
  },

  methods: {
    loadTemplate(templateId) {
      this.loading = true;
      this.$store
        .dispatch('templates/get', templateId)
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    refreshTemplate() {
      this.loadTemplate(this.template.id);
    },
    showRename() {
      this.$refs.rename.showDialog(this.template);
    },
    savePublished() {
      const previous = !this.template.published;
      this.publishing = true;
      this.$store
        .dispatch('templates/updatePublished', this.template)
        .then(() => {
          this.$root.$snackbarSuccess(
            this.template.published ? 'Template published' : 'Template unpublished'
          );
        })
        .catch((error) => {
          this.template.published = previous;
          const msg = error.response && error.response.data && error.response.data.message;
          if (msg) {
            this.$root.$alert('Cannot change publish state', msg);
          } else {
            this.logError(error);
          }
        })
        .finally(() => {
          this.publishing = false;
        });
    },
    refreshImages() {
      this.refreshing = true;
      this.$store
        .dispatch('templates/generateImages', this.template.id)
        .then(() => {
          this.$root.$snackbarSuccess('Image generation started');
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.refreshing = false;
        });
    },
    save() {
      switch (this.tab) {
        case 0:
          this.$refs.details[0].save();
          break;
        case 1:
          this.$refs.project[0].save();
          break;
      }
    },
    async deleteTemplate() {
      if (
        await this.$root.$confirm(
          'Delete Template',
          'Are you sure you want to delete this template?',
          { color: 'error' }
        )
      ) {
        this.deleting = true;
        this.$store
          .dispatch('templates/delete', this.template.id)
          .then(() => {
            this.$root.$snackbarSuccess('Template deleted');
          })
          .catch((error) => {
            this.logError(error);
          })
          .finally(() => {
            this.deleting = false;
          });
      }
    },
    openEditor() {
      this.$store
        .dispatch('templates/getEditorUrl', this.template.id)
        .then((url) => {
          window.open(url, '_blank');
        })
        .catch((error) => {
          this.logError(error);
        });
    },
    showCopy() {
      this.$bus.$emit(this.$enums.EVENT_BUS_EVENTS.COPY_TEMPLATE_SHOW, {
        templateIds: [this.template.id]
      });
    },
    convertToFreedom() {
      this.converting = true;
      this.$store
        .dispatch('templates/convertToFreedom', this.template.id)
        .then(() => {
          this.$root.$snackbarSuccess('Template converted');
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.converting = false;
        });
    },
    previewImages() {
      let imgs = [this.template.imageUrlTransparent];
      imgs = imgs.concat(this.template.previews.map((x) => x.url));
      this.$refs.imagePreview.show(imgs);
    }
  }
};
</script>
